import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { Modal } from '../../components/ui';
import type { Cell, Money } from './types';
import {
	Card,
	Chip,
	DASH,
	ErrorLine,
	FY_OPTIONS,
	GROUPS,
	Group,
	Loading,
	Tile,
	full,
	groupOf,
	href,
	linkCls,
	monthLabel,
	monthShort,
	nowYm,
	short,
	snCls,
	sumOrNull,
	useMatrix,
} from './shared';

/* ── Overview: all sites x months ───────────────────────────────────────────── */

// App green (primary-500 = #00a76f) for the tab, header, shading and total. Full class strings, so Tailwind keeps them.
const T = {
	rgb: '0, 167, 111',
	tab: 'bg-primary-50 text-primary-800 border border-primary-200',
	head: 'bg-gray-50 text-gray-600',
	bar: 'from-white',
	foot: 'bg-gray-50 text-gray-900 border-t border-gray-300',
	ring: 'hover:ring-primary-400',
};

/** Pairs of sheets with the same people and net pay in one month. One row per pair, 13px, plain words. */
const DupTable: React.FC<{
	pairs: [string, string][];
	month: string;
	fy: string;
	cell: (s: string, m: string) => Cell | undefined;
}> = ({ pairs, month, fy, cell }) => {
	const extra = sumOrNull(pairs.map(([, b]) => cell(b, month)?.net_pay ?? null));
	const sheet = (s: string) => {
		const c = cell(s, month)!;
		return (
			<td className='px-3 py-2 align-top'>
				<Link to={href({ fy, site: s, month })} className={`${linkCls} font-medium`}>
					{s}
				</Link>
				<div className='text-gray-500'>
					{c.file_name} · {c.tab_name}
				</div>
			</td>
		);
	};
	return (
		<div className='text-[13px]'>
			<p className='text-gray-600 mb-3'>
				{pairs.length === 1 ? 'Two sheets have' : `${pairs.length} pairs of sheets have`} the same
				people and the same net pay. Both are in the total, so{' '}
				<span className='font-semibold text-amber-700'>{full(extra)}</span> may be counted twice.
				Open each to compare the names.
			</p>
			<table className='w-full'>
				<thead className={T.head}>
					<tr>
						<th className={snCls}>#</th>
						<th className='text-left px-3 py-2 font-semibold'>Sheet</th>
						<th className='text-left px-3 py-2 font-semibold'>Looks the same as</th>
						<th className='text-right px-3 py-2 font-semibold'>People</th>
						<th className='text-right px-3 py-2 font-semibold'>Net pay, each</th>
					</tr>
				</thead>
				<tbody>
					{pairs.map(([a, b], i) => (
						<tr key={b} className='border-t border-gray-100'>
							<td className={`${snCls} align-top`}>{i + 1}</td>
							{sheet(a)}
							{sheet(b)}
							<td className='px-3 py-2 text-right align-top tabular-nums'>
								{cell(a, month)!.people}
							</td>
							<td className='px-3 py-2 text-right align-top tabular-nums font-medium'>
								{full(cell(a, month)!.net_pay)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export const Overview: React.FC<{ fy: string; setFy: (fy: string) => void }> = ({ fy, setFy }) => {
	const { loading, error, sites, months, cell } = useMatrix(fy);
	const [group, setGroup] = useState<Group>('Head office');
	const [find, setFind] = useState('');
	const [pendingMonth, setPendingMonth] = useState<string | null>(null);
	const [dupMonth, setDupMonth] = useState<string | null>(null);
	const [monthsOpen, setMonthsOpen] = useState(false);

	// Tiles and month cards are always the whole company. Tabs + search only pick the table rows.
	const scope = sites.filter(
		s => groupOf(s) === group && s.toLowerCase().includes(find.toLowerCase())
	);
	const groupsShown = [group].filter(g => scope.some(s => groupOf(s) === g));
	const ordered = groupsShown.flatMap(g => scope.filter(s => groupOf(s) === g));

	const colTotal = (m: string, list = sites) =>
		sumOrNull(list.map(s => cell(s, m)?.net_pay ?? null));
	const rowTotal = (s: string) => sumOrNull(months.map(m => cell(s, m)?.net_pay ?? null));
	const peopleIn = (m: string) => sites.reduce((a, s) => a + (cell(s, m)?.people || 0), 0);
	const fyTotal = sumOrNull(months.map(m => colTotal(m)));
	// The wage month we are in is paid next month: its sheets are still coming in. Never call it "latest".
	const done = months.filter(m => m < nowYm && colTotal(m) !== null);
	const lastMonth = done[done.length - 1];
	const prevMonth = done[done.length - 2];
	const lastTotal = lastMonth ? colTotal(lastMonth) : null;
	const prevTotal = prevMonth ? colTotal(prevMonth) : null;
	const change =
		lastTotal !== null && prevTotal ? ((lastTotal - prevTotal) / prevTotal) * 100 : null;
	const gaps = sites.reduce((a, s) => a + months.filter(m => m < nowYm && !cell(s, m)).length, 0);

	// Two sites, same month, same people and same net pay to the paisa = almost surely one tab loaded twice.
	const dupOf = new Map<string, string>();
	months.forEach(m =>
		sites.forEach((a, i) =>
			sites.slice(i + 1).forEach(b => {
				const x = cell(a, m);
				const y = cell(b, m);
				if (x && y && x.people === y.people && x.net_pay !== null && x.net_pay === y.net_pay)
					dupOf.set(`${b}|${m}`, a);
			})
		)
	);
	const dupsInScope = Array.from(dupOf.keys());
	const dupAmount = sumOrNull(
		dupsInScope.map(k => cell(k.split('|')[0], k.split('|')[1])?.net_pay ?? null)
	);

	const max = Math.max(1, ...scope.flatMap(s => months.map(m => cell(s, m)?.net_pay || 0)));
	// Sequential tint in the group's colour: bigger payroll = deeper. Text stays ink, never the tint.
	const tint = (v: Money) =>
		v === null ? undefined : `rgba(${T.rgb}, ${0.03 + 0.12 * (v / max)})`;

	const renderRow = (s: string, muted = false) => (
		<tr key={s} className={`border-t border-gray-100 ${muted ? 'text-gray-500 italic' : ''}`}>
			<td className={snCls}>{muted ? DASH : ordered.indexOf(s) + 1}</td>
			<td
				className={`px-3 py-2 whitespace-nowrap sticky left-0 z-[1] ${muted ? 'bg-gray-50' : 'bg-white'}`}
			>
				<Link
					to={href({ fy, site: s })}
					className={`${linkCls} font-medium inline-flex items-center gap-1 not-italic`}
				>
					{s}
					<ChevronRight className='w-3.5 h-3.5' />
				</Link>
			</td>
			{months.map(m => {
				const c = cell(s, m);
				if (!c)
					return (
						<td key={m} className='px-3 py-2 text-right text-[11px] text-gray-300'>
							{m >= nowYm ? 'not yet' : 'No sheet'}
						</td>
					);
				const dup = dupOf.get(`${s}|${m}`);
				return (
					<td key={m} className='p-0.5'>
						<Link
							to={href({ fy, site: s, month: m })}
							title={`Open ${s} · ${monthLabel(m)}\n${c.file_name} · ${c.tab_name}${dup ? `\nSame people and amount as ${dup}: possible duplicate` : ''}`}
							style={{ background: muted ? undefined : tint(c.net_pay) }}
							className={`block text-right px-2.5 py-1.5 rounded-md hover:ring-2 ${T.ring} transition ${
								dup ? 'ring-2 ring-amber-400' : ''
							}`}
						>
							<div className='font-medium text-gray-900'>{short(c.net_pay)}</div>
							<div className={`text-[11px] ${dup ? 'text-amber-700' : 'text-gray-500'}`}>
								{dup
									? 'possible duplicate'
									: `${c.people} ppl${c.versions_seen > 1 ? ` · v${c.versions_seen}` : ''}`}
							</div>
						</Link>
					</td>
				);
			})}
			<td className='px-3 py-2 text-right font-semibold'>{short(rowTotal(s))}</td>
		</tr>
	);

	return (
		<>
			<div className='flex items-end justify-between mb-4'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900'>Payroll</h1>
					<p className='text-sm text-gray-500'>
						Salary paid at every site, from the final salary sheet. Read-only.
					</p>
				</div>
				<select
					value={fy}
					onChange={e => setFy(e.target.value)}
					className='border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white'
					aria-label='Financial year'
				>
					{FY_OPTIONS.map(f => (
						<option key={f} value={f}>
							FY {f}
						</option>
					))}
				</select>
			</div>
			{loading && <Loading />}
			{error && <ErrorLine msg={error} />}
			{!loading && !sites.length && (
				<div className='text-sm text-gray-500 py-10 text-center'>No salary sheets for FY {fy}.</div>
			)}
			{!loading && sites.length > 0 && (
				<>
					<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
						<Tile
							label={`Net pay so far · FY ${fy}`}
							value={short(fyTotal)}
							sub={
								dupsInScope.length ? (
									<span className='text-amber-700'>
										includes {short(dupAmount)} that may be counted twice
									</span>
								) : (
									`all sites · ${done.length} months`
								)
							}
						/>
						<Tile
							label={lastMonth ? `Last full month · ${monthLabel(lastMonth)}` : 'Last full month'}
							value={short(lastTotal)}
							sub={
								lastMonth
									? `${peopleIn(lastMonth).toLocaleString('en-IN')} people${
											change === null
												? ''
												: ` · ${change >= 0 ? '+' : ''}${change.toFixed(1)}% vs ${monthShort(prevMonth!)}`
										}`
									: undefined
							}
							to={lastMonth ? href({ fy, month: lastMonth }) : undefined}
						/>
						<Tile
							label='Average per month'
							value={short(
								fyTotal === null || !done.length
									? null
									: (sumOrNull(done.map(m => colTotal(m))) || 0) / done.length
							)}
							sub='full months only'
						/>
						<Tile
							label='Missing sheets'
							value={String(gaps)}
							sub={`site-months with no final sheet · ${sites.length} sites`}
						/>
					</div>

					{/* Months: one folded line; open it for a table with a bar per month. */}
					<Card className='mb-4'>
						<button
							onClick={() => setMonthsOpen(o => !o)}
							aria-expanded={monthsOpen}
							className='w-full flex items-center gap-2 px-4 py-3 text-[13px] text-left hover:bg-gray-50 rounded-xl'
						>
							<ChevronRight
								className={`w-4 h-4 text-gray-500 transition ${monthsOpen ? 'rotate-90' : ''}`}
							/>
							<span className='font-semibold text-gray-900'>Month by month</span>
							<span className='text-gray-500'>
								{monthShort(months[0])} – {monthShort(months[months.length - 1])} ·{' '}
								{months
									.filter(m => m < nowYm)
									.reduce(
										(a, m) => a + sites.length - sites.filter(s => cell(s, m)).length,
										0
									)}{' '}
								sheets pending
							</span>
							<span className='ml-auto text-gray-500'>
								{monthsOpen ? 'Hide' : 'Show all months'}
							</span>
						</button>
						{monthsOpen && (
							<table className='w-full text-[13px] border-t border-gray-100'>
								<thead className={T.head}>
									<tr>
										<th className='text-left px-4 py-2 font-semibold w-24'>Month</th>
										<th className='text-right px-3 py-2 font-semibold w-28'>Net pay</th>
										<th className='px-3 py-2' />
										<th className='text-right px-3 py-2 font-semibold w-20'>People</th>
										<th className='text-right px-3 py-2 font-semibold w-28'>Sheets in</th>
										<th className='text-right px-3 py-2 font-semibold w-28'>Pending</th>
										<th className='text-left px-3 py-2 font-semibold w-48'>Note</th>
									</tr>
								</thead>
								<tbody>
									{(() => {
										const top = Math.max(1, ...months.map(m => colTotal(m) || 0));
										return months.map(m => {
											const inCount = sites.filter(s => cell(s, m)).length;
											const pending = sites.length - inCount;
											const open = m >= nowYm;
											const dup = dupsInScope.some(k => k.endsWith(`|${m}`));
											const v = colTotal(m);
											return (
												<tr key={m} className='border-t border-gray-100 hover:bg-gray-50/60'>
													<td className='px-4 py-2'>
														<Link to={href({ fy, month: m })} className={`${linkCls} font-medium`}>
															{monthShort(m)}
														</Link>
													</td>
													<td
														className={`px-3 py-2 text-right tabular-nums font-semibold ${v === null ? 'text-gray-300' : 'text-gray-900'}`}
													>
														{short(v)}
													</td>
													<td className='px-3 py-2'>
														<div className='h-2.5 rounded-full bg-gray-100'>
															<div
																className={`h-full rounded-full ${dup ? 'bg-amber-400/60' : open ? 'bg-gray-300' : 'bg-primary-600/45'}`}
																style={{ width: `${((v || 0) / top) * 100}%` }}
															/>
														</div>
													</td>
													<td className='px-3 py-2 text-right tabular-nums text-gray-600'>
														{peopleIn(m) || DASH}
													</td>
													<td className='px-3 py-2 text-right tabular-nums text-gray-600'>
														{inCount} of {sites.length}
													</td>
													<td className='px-3 py-2 text-right'>
														{pending > 0 ? (
															<button
																onClick={() => setPendingMonth(m)}
																className={`font-medium hover:underline ${open ? 'text-gray-500' : 'text-amber-700'}`}
															>
																{pending} pending
															</button>
														) : (
															<span className='text-primary-700'>all in</span>
														)}
													</td>
													<td className='px-3 py-2'>
														{dup ? (
															<button
																onClick={() => setDupMonth(m)}
																className='text-amber-700 font-medium hover:underline'
															>
																May include a duplicate
															</button>
														) : open ? (
															<span className='text-gray-400'>In progress</span>
														) : null}
													</td>
												</tr>
											);
										});
									})()}
								</tbody>
							</table>
						)}
					</Card>
					<Modal
						open={!!dupMonth}
						onClose={() => setDupMonth(null)}
						size='lg'
						title={dupMonth ? `Possible duplicate · ${monthLabel(dupMonth)}` : ''}
					>
						{dupMonth && (
							<DupTable
								pairs={dupsInScope
									.filter(k => k.endsWith(`|${dupMonth}`))
									.map(k => [dupOf.get(k)!, k.split('|')[0]] as [string, string])}
								month={dupMonth}
								fy={fy}
								cell={cell}
							/>
						)}
					</Modal>
					<Modal
						open={!!pendingMonth}
						onClose={() => setPendingMonth(null)}
						size='sm'
						title={pendingMonth ? `Sheets pending · ${monthLabel(pendingMonth)}` : ''}
					>
						{pendingMonth && (
							<>
								<p className='text-[13px] text-gray-500 mb-3'>
									{pendingMonth >= nowYm
										? 'This wage month is paid next month, so most sheets are not due yet.'
										: 'No final salary sheet found for these sites.'}
								</p>
								<table className='w-full text-[13px]'>
									<tbody>
										{sites
											.filter(s => !cell(s, pendingMonth))
											.map((s, i) => (
												<tr key={s} className='border-t border-gray-100'>
													<td className={snCls}>{i + 1}</td>
													<td className='px-3 py-2'>
														<Link to={href({ fy, site: s })} className={`${linkCls} font-medium`}>
															{s}
														</Link>
													</td>
													<td className='px-3 py-2 text-right text-gray-500'>{groupOf(s)}</td>
												</tr>
											))}
									</tbody>
								</table>
							</>
						)}
					</Modal>

					<Card>
						{/* Tabs pick which sites the table lists. They do not change the cards above. */}
						<div
							className={`flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gradient-to-r ${T.bar} to-white rounded-t-xl`}
						>
							<div role='tablist' className='flex gap-2'>
								{GROUPS.map(g => {
									const on = group === g;
									return (
										<button
											key={g}
											role='tab'
											aria-selected={on}
											onClick={() => setGroup(g)}
											className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition ${on ? T.tab : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
										>
											{g}
											<span
												className={`px-1.5 rounded-full text-[11px] tabular-nums ${on ? 'bg-white/25' : 'bg-gray-100 text-gray-500'}`}
											>
												{sites.filter(s => groupOf(s) === g).length}
											</span>
										</button>
									);
								})}
							</div>
							<div className='relative'>
								<Search className='w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2' />
								<input
									value={find}
									onChange={e => setFind(e.target.value)}
									placeholder='Find a site'
									className='pl-8 pr-3 py-1.5 text-[13px] border border-gray-200 rounded-lg w-52 bg-white'
								/>
							</div>
							<span className='ml-auto flex items-center gap-2'>
								<Chip tone='grey'>Bank: not checked yet</Chip>
								<Chip tone='grey'>Statutory: not checked yet</Chip>
							</span>
						</div>
						<div className='overflow-x-auto'>
							<table className='w-full text-[13px]'>
								<thead className={T.head}>
									<tr>
										<th className={snCls}>#</th>
										<th
											className={`text-left px-3 py-2 font-semibold sticky left-0 z-[1] ${T.head}`}
										>
											Site
										</th>
										{months.map(m => (
											<th key={m} className='text-right px-3 py-2 font-semibold whitespace-nowrap'>
												<Link to={href({ fy, month: m })} className='hover:underline'>
													{monthShort(m)}
												</Link>
											</th>
										))}
										<th className='text-right px-3 py-2 font-semibold whitespace-nowrap'>
											FY total
										</th>
									</tr>
								</thead>
								{!scope.length && (
									<tbody>
										<tr>
											<td colSpan={months.length + 3} className='py-10 text-center text-gray-500'>
												No site matches “{find}”.
											</td>
										</tr>
									</tbody>
								)}
								{groupsShown.map(g => {
									const rows = scope.filter(s => groupOf(s) === g);
									return (
										<tbody key={g}>
											{groupsShown.length > 1 && (
												<tr className='bg-gray-50 border-t border-gray-200'>
													<td />
													<td className='px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 sticky left-0 bg-gray-50 z-[1]'>
														{g} · {rows.length}
													</td>
													{months.map(m => (
														<td
															key={m}
															className='px-3 py-1.5 text-right text-[11px] font-medium text-gray-500'
														>
															{short(colTotal(m, rows))}
														</td>
													))}
													<td className='px-3 py-1.5 text-right text-[11px] font-semibold text-gray-600'>
														{short(sumOrNull(rows.map(rowTotal)))}
													</td>
												</tr>
											)}
											{rows.map(s => renderRow(s))}
										</tbody>
									);
								})}
								<tfoot className={`font-semibold ${T.foot}`}>
									<tr>
										<td />
										<td className={`px-3 py-2.5 sticky left-0 z-[1] ${T.foot}`}>
											{group} total · {scope.length} sites
										</td>
										{months.map(m => (
											<td key={m} className='px-3 py-2.5 text-right'>
												{short(colTotal(m, scope))}
											</td>
										))}
										<td className='px-3 py-2.5 text-right'>
											{short(sumOrNull(scope.map(rowTotal)))}
										</td>
									</tr>
								</tfoot>
							</table>
						</div>
						<div className='px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400'>
							Click a site name for its full year · click an amount for that month&apos;s people
						</div>
					</Card>
				</>
			)}
		</>
	);
};
